/**
 * Login page right panel with animated grid background
 * This component implements a complex Canvas-based animation system
 * The high complexity is due to animation state management and Canvas rendering logic
 * TODO: Consider extracting animation logic into a separate animation engine module
 */
import { useCallback, useEffect, useRef } from 'react';

interface Node {
  x: number;
  y: number;
  row: number;
  col: number;
  id: string;
  connections: Connection[];
  isActive: boolean;
  isSource: boolean;
  isEndNode: boolean;
  isWarning: boolean;
  isWhite: boolean;
  isVisible: boolean;
  activationProgress: number;
}

interface Connection {
  from: Node;
  to: Node;
  key: string;
  progress: number;
  isWarning: boolean;
  isWhite: boolean;
  isVisible: boolean;
  speed: number;
  easingType: number;
  easingStrength: number;
  completed: boolean;
  isHorizontal: boolean;
  isVertical: boolean;
}

interface ActiveConnection {
  connection: Connection;
  progress: number;
  startNode: Node;
  speed: number;
}

interface WarningSymbol {
  x: number;
  y: number;
  opacity: number;
  fadeSpeed: number;
  fadeOut?: boolean;
  fadeDelay?: number;
  currentDelay?: number;
}

interface CleanupAnimation {
  connection?: Connection;
  node?: Node;
  progress: number;
  delay: number;
  currentDelay: number;
  type: 'connection' | 'node';
}

interface GridBuildAnimation {
  connection: Connection;
  delay: number;
  duration: number;
  startTime: number;
  opacity: number;
  started: boolean;
}

interface GridConfig {
  staticCanvas: HTMLCanvasElement | null;
  animCanvas: HTMLCanvasElement | null;
  completedCanvas: HTMLCanvasElement | null;
  staticCtx: CanvasRenderingContext2D | null;
  animCtx: CanvasRenderingContext2D | null;
  completedCtx: CanvasRenderingContext2D | null;
  gridSize: number;
  nodes: Node[];
  connections: Connection[];
  activeNodes: Set<Node>;
  activeConnections: Map<string, ActiveConnection>;
  completedConnections: Set<Connection>;
  completedNodes: Set<Node>;
  warningSymbols: Map<string, WarningSymbol>;
  sourceNodes: Set<Node>;
  isCleaningUp: boolean;
  cleanupAnimations: Map<string, CleanupAnimation>;
  cleanedConnections: Set<Connection>;
  waitingForCleanup: boolean;
  cleanupDelay: number;
  cleanupTimer: number;
  waitingForReset: boolean;
  resetDelay: number;
  resetTimer: number;
  isGridBuilding: boolean;
  gridBuildAnimations: GridBuildAnimation[];
  gridBuildProgress: number;
  hoveredNode: Node | null;
  isResetting: boolean;
  hasStartedSpreading: boolean;
  isFadingOut: boolean;
  fadeOutProgress: number;
  fadeOutSpeed: number;
  lastTime: number;
  enableWarnings: boolean;
  warningChance: number;
  whiteConnectionChance: number;
  nodeVisibilityChance: number;
  connectionDrawChance: number;
  autoTriggerTimer: number;
  autoTriggerDelay: number;
  isAutoTriggerEnabled: boolean;
  animFrameId: number;
  colors: {
    background: string;
    gridInactive: string;
    gridActive: string;
    gridWarning: string;
    gridWhite: string;
    nodeInactive: string;
    nodeActive: string;
    nodeWarning: string;
    nodeWhite: string;
    nodeHover: string;
  };
}

export function RightPanel() {
  const configRef = useRef<GridConfig>({
    staticCanvas: null,
    animCanvas: null,
    completedCanvas: null,
    staticCtx: null,
    animCtx: null,
    completedCtx: null,
    gridSize: 80,
    nodes: [],
    connections: [],
    activeNodes: new Set(),
    activeConnections: new Map(),
    completedConnections: new Set(),
    completedNodes: new Set(),
    warningSymbols: new Map(),
    sourceNodes: new Set(),
    isCleaningUp: false,
    cleanupAnimations: new Map(),
    cleanedConnections: new Set(),
    waitingForCleanup: false,
    cleanupDelay: 1000,
    cleanupTimer: 0,
    waitingForReset: false,
    resetDelay: 2000,
    resetTimer: 0,
    isGridBuilding: false,
    gridBuildAnimations: [],
    gridBuildProgress: 0,
    hoveredNode: null,
    isResetting: false,
    hasStartedSpreading: false,
    isFadingOut: false,
    fadeOutProgress: 0,
    fadeOutSpeed: 0.02,
    lastTime: Date.now(),
    enableWarnings: true,
    warningChance: 0.15,
    whiteConnectionChance: 0.25,
    nodeVisibilityChance: 0.4,
    connectionDrawChance: 0.6,
    autoTriggerTimer: 0,
    autoTriggerDelay: 5000,
    isAutoTriggerEnabled: true,
    animFrameId: 0,
    colors: {
      background: '#0a0a0a',
      gridInactive: '#333333',
      gridActive: '#00ff88',
      gridWarning: '#ffffff',
      gridWhite: '#ffffff',
      nodeInactive: '#666666',
      nodeActive: '#00ff88',
      nodeWarning: '#ffffff',
      nodeWhite: '#ffffff',
      nodeHover: '#888888'
    }
  });

  const createNode = useCallback((x: number, y: number, row: number, col: number): Node => {
    return {
      x,
      y,
      row,
      col,
      id: `${row},${col}`,
      connections: [],
      isActive: false,
      isSource: false,
      isEndNode: false,
      isWarning: false,
      isWhite: false,
      isVisible: false,
      activationProgress: 0
    };
  }, []);

  const createConnection = useCallback((from: Node, to: Node): Connection => {
    const config = configRef.current;
    const isWarning = config.enableWarnings && Math.random() < config.warningChance;
    const isWhite = !isWarning && Math.random() < config.whiteConnectionChance;

    const rand = Math.random();
    let speed;
    if (rand < 0.6) {
      speed = 0.008 + Math.random() * 0.012;
    } else if (rand < 0.9) {
      speed = 0.02 + Math.random() * 0.02;
    } else {
      speed = 0.045 + Math.random() * 0.025;
    }

    return {
      from,
      to,
      key: `${from.id}-${to.id}`,
      progress: 0,
      isWarning,
      isWhite,
      isVisible: false,
      speed,
      easingType: Math.random(),
      easingStrength: 0.5 + Math.random() * 1.5,
      completed: false,
      isHorizontal: from.row === to.row,
      isVertical: from.col === to.col
    };
  }, []);

  const getEasedProgress = useCallback((conn: Connection): number => {
    const t = conn.progress;

    if (conn.easingType < 0.3) {
      return 1 - Math.pow(1 - t, 2 + conn.easingStrength);
    } else if (conn.easingType < 0.6) {
      return Math.pow(t, 1.5 + conn.easingStrength);
    } else if (conn.easingType < 0.8) {
      return t < 0.5 ? 2 * Math.pow(t, 2) : 1 - Math.pow(-2 * t + 2, 2) / 2;
    } else {
      return t;
    }
  }, []);

  const createGrid = useCallback(() => {
    const config = configRef.current;
    if (!config.staticCanvas || !config.completedCanvas) return;

    config.nodes = [];
    config.connections = [];
    config.activeNodes.clear();
    config.activeConnections.clear();
    config.completedConnections.clear();
    config.completedNodes.clear();
    config.warningSymbols.clear();
    config.sourceNodes.clear();
    config.cleanupAnimations.clear();
    config.cleanedConnections.clear();
    config.isCleaningUp = false;
    config.waitingForCleanup = false;
    config.cleanupTimer = 0;
    config.waitingForReset = false;
    config.resetTimer = 0;
    config.isGridBuilding = true;
    config.gridBuildAnimations = [];
    config.gridBuildProgress = 0;
    config.autoTriggerTimer = 0;

    const width = config.staticCanvas.width;
    const height = config.staticCanvas.height;
    const cols = Math.ceil(width / config.gridSize) + 2;
    const rows = Math.ceil(height / config.gridSize) + 2;

    const offsetX = (width - (cols - 1) * config.gridSize) / 2;
    const offsetY = (height - (rows - 1) * config.gridSize) / 2;

    const nodeMap = new Map<string, Node>();
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = col * config.gridSize + offsetX;
        const y = row * config.gridSize + offsetY;
        const node = createNode(x, y, row, col);
        config.nodes.push(node);
        nodeMap.set(node.id, node);
      }
    }

    for (const node of config.nodes) {
      const rightId = `${node.row},${node.col + 1}`;
      const rightNode = nodeMap.get(rightId);
      if (rightNode) {
        const conn = createConnection(node, rightNode);
        config.connections.push(conn);
        node.connections.push(conn);
        rightNode.connections.push(conn);
      }

      const downId = `${node.row + 1},${node.col}`;
      const downNode = nodeMap.get(downId);
      if (downNode) {
        const conn = createConnection(node, downNode);
        config.connections.push(conn);
        node.connections.push(conn);
        downNode.connections.push(conn);
      }
    }

    if (config.staticCtx) {
      config.staticCtx.fillStyle = config.colors.background;
      config.staticCtx.fillRect(0, 0, config.staticCanvas.width, config.staticCanvas.height);
    }
    if (config.completedCtx) {
      config.completedCtx.clearRect(
        0,
        0,
        config.completedCanvas.width,
        config.completedCanvas.height
      );
    }

    const center = { x: width / 2, y: height / 2 };
    config.gridBuildAnimations = config.connections.map((conn) => {
      const mx = (conn.from.x + conn.to.x) / 2;
      const my = (conn.from.y + conn.to.y) / 2;
      const dist = Math.sqrt((mx - center.x) ** 2 + (my - center.y) ** 2);
      const delay = 0.1 + dist * 0.0008 + Math.random() * 0.3;
      const duration = 0.4 + Math.random() * 0.3;

      return {
        connection: conn,
        delay,
        duration,
        startTime: 0,
        opacity: 0,
        started: false
      };
    });
  }, [createNode, createConnection]);

  const drawStaticGrid = useCallback(() => {
    const config = configRef.current;
    if (!config.staticCtx || !config.staticCanvas) return;

    const ctx = config.staticCtx;
    ctx.fillStyle = config.colors.background;
    ctx.fillRect(0, 0, config.staticCanvas.width, config.staticCanvas.height);

    ctx.strokeStyle = config.colors.gridInactive;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);

    ctx.beginPath();
    for (const conn of config.connections) {
      ctx.moveTo(conn.from.x, conn.from.y);
      ctx.lineTo(conn.to.x, conn.to.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    const size = 6;
    ctx.strokeStyle = config.colors.nodeInactive;
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    for (const node of config.nodes) {
      ctx.moveTo(node.x - size, node.y);
      ctx.lineTo(node.x + size, node.y);
      ctx.moveTo(node.x, node.y - size);
      ctx.lineTo(node.x, node.y + size);
    }
    ctx.stroke();
  }, []);

  const drawCompletedConnection = useCallback((conn: Connection) => {
    const config = configRef.current;
    if (!config.completedCtx || !conn.isVisible) return;

    const ctx = config.completedCtx;
    let color;
    if (conn.isWarning) {
      color = config.colors.gridWarning;
    } else if (conn.isWhite) {
      color = config.colors.gridWhite;
    } else {
      color = config.colors.gridActive;
    }

    ctx.save();
    ctx.shadowBlur = conn.isWarning || conn.isWhite ? 25 : 20;
    ctx.shadowColor = color;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(conn.from.x, conn.from.y);
    ctx.lineTo(conn.to.x, conn.to.y);
    ctx.stroke();

    ctx.restore();
  }, []);

  const drawPlayButton = useCallback(
    (ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string) => {
      ctx.save();

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      const triangleSize = radius * 0.5;
      ctx.beginPath();
      ctx.moveTo(x - triangleSize * 0.3, y - triangleSize);
      ctx.lineTo(x - triangleSize * 0.3, y + triangleSize);
      ctx.lineTo(x + triangleSize * 0.9, y);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    },
    []
  );

  const drawStopButton = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      radius: number,
      color: string,
      isWhiteNode = false
    ) => {
      ctx.save();

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = isWhiteNode ? '#000000' : '#ffffff';
      const squareSize = radius * 0.6;
      ctx.fillRect(x - squareSize / 2, y - squareSize / 2, squareSize, squareSize);

      ctx.restore();
    },
    []
  );

  const drawWarningSymbol = useCallback(
    (ctx: CanvasRenderingContext2D, x: number, y: number, opacity = 1) => {
      const triangleSize = 10;

      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.shadowBlur = 0;
      ctx.strokeStyle = '#ff3030';
      ctx.fillStyle = '#ffffff';
      ctx.lineWidth = 1.5;

      ctx.beginPath();
      ctx.moveTo(x, y - triangleSize);
      ctx.lineTo(x - triangleSize * 0.866, y + triangleSize * 0.5);
      ctx.lineTo(x + triangleSize * 0.866, y + triangleSize * 0.5);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#ff3030';
      ctx.fillRect(x - 1, y - 5, 2, 6);
      ctx.fillRect(x - 1, y + 3, 2, 2);

      ctx.restore();
    },
    []
  );

  const drawAnimation = useCallback(() => {
    const config = configRef.current;
    if (!config.animCtx || !config.animCanvas) return;

    const ctx = config.animCtx;
    ctx.clearRect(0, 0, config.animCanvas.width, config.animCanvas.height);

    const fadeAlpha = config.isFadingOut ? 1 - config.fadeOutProgress : 1;
    if (fadeAlpha <= 0) return;

    // Draw cleanup animations
    if (config.isCleaningUp && config.cleanupAnimations.size > 0) {
      for (const [, anim] of config.cleanupAnimations) {
        if (anim.type !== 'connection') continue;
        if (anim.currentDelay < anim.delay) continue;
        if (!anim.connection) continue;

        const conn = anim.connection;
        const progress = anim.progress;

        const r = Math.round(255 * (1 - progress));
        const g = 255;
        const b = Math.round(255 + (136 - 255) * progress);
        const color = `rgb(${r}, ${g}, ${b})`;

        ctx.save();
        ctx.shadowBlur = 25;
        ctx.shadowColor = color;
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.moveTo(conn.from.x, conn.from.y);
        ctx.lineTo(conn.to.x, conn.to.y);
        ctx.stroke();

        ctx.restore();
      }
    }

    // Draw growing lines - grouped by type for performance
    if (config.activeConnections.size > 0) {
      // Draw normal (green) lines
      ctx.save();
      ctx.shadowBlur = 20;
      ctx.shadowColor = config.colors.gridActive;
      ctx.strokeStyle = config.colors.gridActive;
      ctx.lineWidth = 2;

      for (const [, anim] of config.activeConnections) {
        if (!anim.connection.isWarning && !anim.connection.isWhite) {
          const startX = anim.startNode.x;
          const startY = anim.startNode.y;
          const conn = anim.connection;
          const endNode = conn.from === anim.startNode ? conn.to : conn.from;

          const easedProgress = getEasedProgress(conn);
          const endX = startX + (endNode.x - startX) * easedProgress;
          const endY = startY + (endNode.y - startY) * easedProgress;

          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, endY);
          ctx.stroke();
        }
      }
      ctx.restore();

      // Draw white lines
      ctx.save();
      ctx.shadowBlur = 25;
      ctx.shadowColor = config.colors.gridWhite;
      ctx.strokeStyle = config.colors.gridWhite;
      ctx.lineWidth = 2;

      for (const [, anim] of config.activeConnections) {
        if (anim.connection.isWhite && !anim.connection.isWarning) {
          const startX = anim.startNode.x;
          const startY = anim.startNode.y;
          const conn = anim.connection;
          const endNode = conn.from === anim.startNode ? conn.to : conn.from;

          const easedProgress = getEasedProgress(conn);
          const endX = startX + (endNode.x - startX) * easedProgress;
          const endY = startY + (endNode.y - startY) * easedProgress;

          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, endY);
          ctx.stroke();
        }
      }
      ctx.restore();

      // Draw warning lines
      ctx.save();
      ctx.shadowBlur = 25;
      ctx.shadowColor = config.colors.gridWarning;
      ctx.strokeStyle = config.colors.gridWarning;
      ctx.lineWidth = 2;

      for (const [, anim] of config.activeConnections) {
        if (anim.connection.isWarning) {
          const startX = anim.startNode.x;
          const startY = anim.startNode.y;
          const conn = anim.connection;
          const endNode = conn.from === anim.startNode ? conn.to : conn.from;

          const easedProgress = getEasedProgress(conn);
          const endX = startX + (endNode.x - startX) * easedProgress;
          const endY = startY + (endNode.y - startY) * easedProgress;

          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, endY);
          ctx.stroke();
        }
      }
      ctx.restore();
    }

    // Draw warning symbols
    for (const [, symbol] of config.warningSymbols) {
      if (symbol.opacity > 0) {
        drawWarningSymbol(ctx, symbol.x, symbol.y, symbol.opacity);
      }
    }

    // Draw nodes
    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    ctx.shadowBlur = 15;
    ctx.shadowColor = config.colors.nodeActive;
    ctx.fillStyle = config.colors.nodeActive;

    for (const node of config.completedNodes) {
      if (!node.isWarning && !node.isWhite && node.isVisible && !node.isSource && !node.isEndNode) {
        const size = 5;
        ctx.fillRect(node.x - size, node.y - size, size * 2, size * 2);
      }
    }
    for (const node of config.activeNodes) {
      if (!node.isWarning && !node.isWhite && node.isVisible && !node.isSource && !node.isEndNode) {
        const t = Math.min(1, node.activationProgress);
        const eased = t < 1 ? -(t * (t - 2)) : 1;
        const size = 5;
        const finalSize = size * eased;
        ctx.fillRect(node.x - finalSize, node.y - finalSize, finalSize * 2, finalSize * 2);
      }
    }
    ctx.restore();

    // Draw source nodes as play buttons
    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    ctx.shadowBlur = 20;
    ctx.shadowColor = config.colors.nodeActive;

    for (const node of config.completedNodes) {
      if (node.isSource && !node.isWarning && !node.isWhite) {
        drawPlayButton(ctx, node.x, node.y, 10, config.colors.nodeActive);
      }
    }
    for (const node of config.activeNodes) {
      if (node.isSource && !node.isWarning && !node.isWhite) {
        const t = Math.min(1, node.activationProgress);
        const eased = t < 1 ? -(t * (t - 2)) : 1;
        const size = 10 * eased;
        drawPlayButton(ctx, node.x, node.y, size, config.colors.nodeActive);
      }
    }
    ctx.restore();

    // Draw end nodes
    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    ctx.shadowBlur = 15;
    ctx.shadowColor = config.colors.nodeActive;

    for (const node of config.completedNodes) {
      if (node.isEndNode && !node.isWarning && !node.isWhite) {
        drawStopButton(ctx, node.x, node.y, 8, config.colors.nodeActive);
      }
    }
    for (const node of config.activeNodes) {
      if (node.isEndNode && !node.isWarning && !node.isWhite) {
        const t = Math.min(1, node.activationProgress);
        const eased = t < 1 ? -(t * (t - 2)) : 1;
        const size = 8 * eased;
        drawStopButton(ctx, node.x, node.y, size, config.colors.nodeActive);
      }
    }
    ctx.restore();

    // Draw white nodes
    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    ctx.shadowBlur = 15;
    ctx.shadowColor = config.colors.nodeWhite;
    ctx.fillStyle = config.colors.nodeWhite;

    for (const node of config.completedNodes) {
      if (node.isWhite && !node.isWarning && node.isVisible && !node.isSource && !node.isEndNode) {
        const size = 5;
        ctx.fillRect(node.x - size, node.y - size, size * 2, size * 2);
      }
    }
    for (const node of config.activeNodes) {
      if (node.isWhite && !node.isWarning && node.isVisible && !node.isSource && !node.isEndNode) {
        const t = Math.min(1, node.activationProgress);
        const eased = t < 1 ? -(t * (t - 2)) : 1;
        const size = 5;
        const finalSize = size * eased;
        ctx.fillRect(node.x - finalSize, node.y - finalSize, finalSize * 2, finalSize * 2);
      }
    }
    ctx.restore();

    // Draw white source nodes
    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    ctx.shadowBlur = 20;
    ctx.shadowColor = config.colors.nodeWhite;
    for (const node of config.completedNodes) {
      if (node.isSource && node.isWhite && !node.isWarning) {
        drawPlayButton(ctx, node.x, node.y, 10, config.colors.nodeWhite);
      }
    }
    for (const node of config.activeNodes) {
      if (node.isSource && node.isWhite && !node.isWarning) {
        const t = Math.min(1, node.activationProgress);
        const eased = t < 1 ? -(t * (t - 2)) : 1;
        const size = 10 * eased;
        drawPlayButton(ctx, node.x, node.y, size, config.colors.nodeWhite);
      }
    }
    ctx.restore();

    // Draw white end nodes
    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    ctx.shadowBlur = 15;
    ctx.shadowColor = config.colors.nodeWhite;
    for (const node of config.completedNodes) {
      if (node.isEndNode && node.isWhite && !node.isWarning) {
        drawStopButton(ctx, node.x, node.y, 8, config.colors.nodeWhite, true);
      }
    }
    for (const node of config.activeNodes) {
      if (node.isEndNode && node.isWhite && !node.isWarning) {
        const t = Math.min(1, node.activationProgress);
        const eased = t < 1 ? -(t * (t - 2)) : 1;
        const size = 8 * eased;
        drawStopButton(ctx, node.x, node.y, size, config.colors.nodeWhite, true);
      }
    }
    ctx.restore();

    // Draw warning nodes
    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    ctx.shadowBlur = 15;
    ctx.shadowColor = config.colors.nodeWarning;
    ctx.fillStyle = config.colors.nodeWarning;

    for (const node of config.completedNodes) {
      if (node.isWarning && node.isVisible && !node.isSource && !node.isEndNode) {
        const size = 5;
        ctx.fillRect(node.x - size, node.y - size, size * 2, size * 2);
      }
    }
    for (const node of config.activeNodes) {
      if (node.isWarning && node.isVisible && !node.isSource && !node.isEndNode) {
        const t = Math.min(1, node.activationProgress);
        const eased = t < 1 ? -(t * (t - 2)) : 1;
        const size = 5;
        const finalSize = size * eased;
        ctx.fillRect(node.x - finalSize, node.y - finalSize, finalSize * 2, finalSize * 2);
      }
    }
    ctx.restore();

    // Draw warning source nodes
    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    ctx.shadowBlur = 20;
    ctx.shadowColor = config.colors.nodeWarning;
    for (const node of config.completedNodes) {
      if (node.isSource && node.isWarning) {
        drawPlayButton(ctx, node.x, node.y, 10, config.colors.nodeWarning);
      }
    }
    for (const node of config.activeNodes) {
      if (node.isSource && node.isWarning) {
        const t = Math.min(1, node.activationProgress);
        const eased = t < 1 ? -(t * (t - 2)) : 1;
        const size = 10 * eased;
        drawPlayButton(ctx, node.x, node.y, size, config.colors.nodeWarning);
      }
    }
    ctx.restore();

    // Draw warning end nodes
    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    ctx.shadowBlur = 15;
    ctx.shadowColor = config.colors.nodeWarning;
    for (const node of config.completedNodes) {
      if (node.isEndNode && node.isWarning) {
        drawStopButton(ctx, node.x, node.y, 8, config.colors.nodeWarning);
      }
    }
    for (const node of config.activeNodes) {
      if (node.isEndNode && node.isWarning) {
        const t = Math.min(1, node.activationProgress);
        const eased = t < 1 ? -(t * (t - 2)) : 1;
        const size = 8 * eased;
        drawStopButton(ctx, node.x, node.y, size, config.colors.nodeWarning);
      }
    }
    ctx.restore();

    // Draw hover
    if (config.hoveredNode && !config.hoveredNode.isActive) {
      const size = 8;
      ctx.save();
      ctx.strokeStyle = config.colors.nodeHover;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.6;

      ctx.strokeRect(config.hoveredNode.x - size, config.hoveredNode.y - size, size * 2, size * 2);

      ctx.restore();
    }
  }, [getEasedProgress, drawPlayButton, drawStopButton, drawWarningSymbol]);

  const spread = useCallback((node: Node, isUserClick = false) => {
    const config = configRef.current;
    if (!config.animCanvas) return;

    const isOutsideBounds =
      node.x < 0 ||
      node.x > config.animCanvas.width ||
      node.y < 0 ||
      node.y > config.animCanvas.height;

    if (isOutsideBounds) {
      node.isEndNode = true;
      return;
    }

    const availableConnections = node.connections.filter((conn) => {
      return !config.activeConnections.has(conn.key);
    });

    if (availableConnections.length === 0) {
      return;
    }

    config.hasStartedSpreading = true;

    if (!node.isActive) {
      node.isActive = true;
      node.activationProgress = 0;
      config.activeNodes.add(node);
    }

    let numToActivate;

    if (isUserClick) {
      node.isSource = true;
      node.isVisible = true;
      config.sourceNodes.add(node);
      numToActivate = 1 + Math.floor(Math.random() * 3);
    } else if (!node.isVisible && Math.random() < 0.3) {
      node.isVisible = true;

      if (Math.random() < 0.2) {
        numToActivate = 0;
        node.isEndNode = true;
      } else {
        numToActivate = 1 + Math.floor(Math.random() * 3);
      }
    } else if (node.isVisible) {
      numToActivate = 1 + Math.floor(Math.random() * 3);
    } else {
      numToActivate = 1;
    }

    numToActivate = Math.min(numToActivate, availableConnections.length);

    const connectionsToActivate = [];
    const available = [...availableConnections];
    for (let i = 0; i < numToActivate; i++) {
      const randomIndex = i + Math.floor(Math.random() * (available.length - i));
      connectionsToActivate.push(available[randomIndex]);
      available[randomIndex] = available[i];
    }

    for (const conn of connectionsToActivate) {
      config.activeConnections.set(conn.key, {
        connection: conn,
        progress: 0,
        startNode: node,
        speed: conn.speed
      });
    }
  }, []);

  const startCleanup = useCallback(() => {
    const config = configRef.current;
    config.isCleaningUp = true;

    for (const conn of config.completedConnections) {
      if (conn.isWarning || conn.isWhite) {
        config.cleanupAnimations.set(conn.key, {
          connection: conn,
          progress: 0,
          delay: Math.random() * 0.5,
          currentDelay: 0,
          type: 'connection'
        });
      }
    }

    for (const node of config.completedNodes) {
      if (node.isWarning || node.isWhite) {
        const key = `node-${node.id}`;
        config.cleanupAnimations.set(key, {
          node: node,
          progress: 0,
          delay: Math.random() * 0.5,
          currentDelay: 0,
          type: 'node'
        });
      }
    }

    for (const [, symbol] of config.warningSymbols) {
      symbol.fadeOut = true;
      symbol.fadeDelay = Math.random() * 0.3;
      symbol.currentDelay = 0;
    }
  }, []);

  const resetGrid = useCallback(() => {
    const config = configRef.current;
    if (config.isResetting) return;
    config.isResetting = true;

    if (config.completedCtx && config.staticCtx) {
      config.completedCtx.globalAlpha = 1;
      config.staticCtx.globalAlpha = 1;
    }

    createGrid();
    config.hasStartedSpreading = false;
    config.isFadingOut = false;
    config.fadeOutProgress = 0;
    config.isResetting = false;
  }, [createGrid]);

  const update = useCallback(
    (delta: number) => {
      const config = configRef.current;

      if (config.isGridBuilding && config.staticCtx && config.staticCanvas) {
        const ctx = config.staticCtx;
        const currentTime = config.gridBuildProgress;

        ctx.fillStyle = config.colors.background;
        ctx.fillRect(0, 0, config.staticCanvas.width, config.staticCanvas.height);

        let allDone = true;
        ctx.setLineDash([5, 5]);

        for (const anim of config.gridBuildAnimations) {
          if (!anim.started && currentTime >= anim.delay) {
            anim.started = true;
            anim.startTime = currentTime;
          }

          if (anim.started) {
            const elapsed = currentTime - anim.startTime;
            anim.opacity = Math.min(1, elapsed / anim.duration);

            if (anim.opacity < 1) {
              allDone = false;
            }
          } else {
            allDone = false;
          }

          if (anim.opacity > 0) {
            const conn = anim.connection;

            ctx.save();
            ctx.globalAlpha = anim.opacity;
            ctx.strokeStyle = config.colors.gridInactive;
            ctx.lineWidth = 1;

            ctx.beginPath();
            ctx.moveTo(conn.from.x, conn.from.y);
            ctx.lineTo(conn.to.x, conn.to.y);
            ctx.stroke();

            ctx.restore();
          }
        }

        ctx.setLineDash([]);

        const size = 6;
        const avgOpacity =
          config.gridBuildAnimations.reduce((sum, anim) => sum + anim.opacity, 0) /
          config.gridBuildAnimations.length;

        if (avgOpacity > 0) {
          ctx.save();
          ctx.globalAlpha = avgOpacity;
          ctx.strokeStyle = config.colors.nodeInactive;
          ctx.lineWidth = 1.5;

          ctx.beginPath();
          for (const node of config.nodes) {
            ctx.moveTo(node.x - size, node.y);
            ctx.lineTo(node.x + size, node.y);
            ctx.moveTo(node.x, node.y - size);
            ctx.lineTo(node.x, node.y + size);
          }
          ctx.stroke();
          ctx.restore();
        }

        config.gridBuildProgress += delta / 1000;

        if (allDone) {
          config.isGridBuilding = false;
          drawStaticGrid();
        }

        return;
      }

      // Update warning symbols
      for (const [key, symbol] of config.warningSymbols) {
        if (symbol.fadeOut) {
          if (
            symbol.currentDelay !== undefined &&
            symbol.fadeDelay !== undefined &&
            symbol.currentDelay < symbol.fadeDelay
          ) {
            symbol.currentDelay += delta / 1000;
            continue;
          }

          symbol.opacity -= 0.05 * (delta / 16);
          if (symbol.opacity <= 0) {
            config.warningSymbols.delete(key);
          }
        } else if (symbol.opacity < 1) {
          symbol.opacity += symbol.fadeSpeed * (delta / 16);
          if (symbol.opacity > 1) {
            symbol.opacity = 1;
          }
        }
      }

      // Update cleanup animations
      if (config.isCleaningUp) {
        const toRemoveCleanup = [];
        const cleanupSpeed = 0.03;

        for (const [key, anim] of config.cleanupAnimations) {
          if (anim.currentDelay < anim.delay) {
            anim.currentDelay += delta / 1000;
            continue;
          }

          if (anim.progress < 1) {
            anim.progress += cleanupSpeed * (delta / 16);

            if (anim.progress >= 1) {
              anim.progress = 1;

              if (anim.type === 'connection' && anim.connection) {
                config.cleanedConnections.add(anim.connection);
                anim.connection.isWarning = false;
                anim.connection.isWhite = false;
              } else if (anim.type === 'node' && anim.node) {
                anim.node.isWarning = false;
                anim.node.isWhite = false;
              }

              toRemoveCleanup.push(key);
            }
          }
        }

        for (const key of toRemoveCleanup) {
          config.cleanupAnimations.delete(key);
        }

        if (toRemoveCleanup.length > 0 && config.completedCtx && config.completedCanvas) {
          config.completedCtx.clearRect(
            0,
            0,
            config.completedCanvas.width,
            config.completedCanvas.height
          );
          for (const conn of config.completedConnections) {
            drawCompletedConnection(conn);
          }
        }

        if (
          config.cleanupAnimations.size === 0 &&
          config.warningSymbols.size === 0 &&
          !config.waitingForReset &&
          !config.isFadingOut
        ) {
          config.waitingForReset = true;
          config.resetTimer = 0;
        }

        if (config.waitingForReset) {
          config.resetTimer += delta;
          if (config.resetTimer >= config.resetDelay) {
            config.waitingForReset = false;
            config.isCleaningUp = false;
            config.isFadingOut = true;
            config.fadeOutProgress = 0;
          }
        }

        return;
      }

      // Update node activation progress
      const toRemoveNodes = [];
      for (const node of config.activeNodes) {
        if (node.activationProgress < 1) {
          node.activationProgress += 0.08 * (delta / 16);
          if (node.activationProgress > 1) {
            node.activationProgress = 1;
            toRemoveNodes.push(node);
          }
        }
      }

      // Update connection animations
      const toRemoveConns = [];
      for (const [key, anim] of [...config.activeConnections]) {
        if (!anim.connection.completed) {
          anim.connection.progress += anim.speed * (delta / 16);

          if (anim.connection.progress >= 1) {
            anim.connection.progress = 1;
            anim.connection.completed = true;

            const targetNode =
              anim.connection.from === anim.startNode ? anim.connection.to : anim.connection.from;

            // Set node properties based on connection type
            if (anim.connection.isWarning) {
              targetNode.isWarning = true;
            } else if (anim.connection.isWhite) {
              targetNode.isWhite = true;
            }

            spread(targetNode);

            anim.connection.isVisible = true;

            drawCompletedConnection(anim.connection);
            config.completedConnections.add(anim.connection);
            toRemoveConns.push(key);

            if (anim.connection.isWarning && anim.connection.isVisible) {
              const midX = (anim.connection.from.x + anim.connection.to.x) / 2;
              const midY = (anim.connection.from.y + anim.connection.to.y) / 2;
              config.warningSymbols.set(anim.connection.key, {
                x: midX,
                y: midY,
                opacity: 0,
                fadeSpeed: 0.05
              });
            }
          }
        } else {
          toRemoveConns.push(key);
        }
      }

      for (const node of toRemoveNodes) {
        config.activeNodes.delete(node);
        config.completedNodes.add(node);
      }

      for (const key of toRemoveConns) {
        config.activeConnections.delete(key);
      }

      if (
        config.hasStartedSpreading &&
        config.activeConnections.size === 0 &&
        !config.waitingForCleanup &&
        !config.isCleaningUp &&
        !config.isFadingOut
      ) {
        config.waitingForCleanup = true;
        config.cleanupTimer = 0;
      }

      if (config.waitingForCleanup) {
        config.cleanupTimer += delta;
        if (config.cleanupTimer >= config.cleanupDelay) {
          config.waitingForCleanup = false;
          startCleanup();
        }
      }

      if (config.isFadingOut) {
        config.fadeOutProgress += config.fadeOutSpeed * (delta / 16);

        if (
          config.fadeOutProgress >= 1 &&
          config.completedCtx &&
          config.staticCtx &&
          config.staticCanvas
        ) {
          config.completedCtx.globalAlpha = 1;
          resetGrid();
        } else if (config.completedCtx && config.staticCtx && config.staticCanvas) {
          const fadeAlpha = 1 - config.fadeOutProgress;
          config.completedCtx.globalAlpha = fadeAlpha;

          const ctx = config.staticCtx;
          ctx.clearRect(0, 0, config.staticCanvas.width, config.staticCanvas.height);
          ctx.fillStyle = config.colors.background;
          ctx.fillRect(0, 0, config.staticCanvas.width, config.staticCanvas.height);

          ctx.save();
          ctx.globalAlpha = fadeAlpha;
          ctx.strokeStyle = config.colors.gridInactive;
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 5]);

          ctx.beginPath();
          for (const conn of config.connections) {
            ctx.moveTo(conn.from.x, conn.from.y);
            ctx.lineTo(conn.to.x, conn.to.y);
          }
          ctx.stroke();
          ctx.setLineDash([]);

          const size = 6;
          ctx.strokeStyle = config.colors.nodeInactive;
          ctx.lineWidth = 1.5;

          ctx.beginPath();
          for (const node of config.nodes) {
            ctx.moveTo(node.x - size, node.y);
            ctx.lineTo(node.x + size, node.y);
            ctx.moveTo(node.x, node.y - size);
            ctx.lineTo(node.x, node.y + size);
          }
          ctx.stroke();
          ctx.restore();
        }
      }

      // Auto-trigger animation after inactivity
      if (
        config.isAutoTriggerEnabled &&
        !config.isGridBuilding &&
        !config.hasStartedSpreading &&
        !config.isCleaningUp &&
        !config.waitingForCleanup &&
        !config.waitingForReset &&
        !config.isFadingOut &&
        config.nodes.length > 0
      ) {
        config.autoTriggerTimer += delta;

        if (config.autoTriggerTimer >= config.autoTriggerDelay) {
          // Select 1-3 random nodes to trigger
          const numNodesToTrigger = 1 + Math.floor(Math.random() * 3); // 1, 2, or 3
          const triggeredIndices = new Set<number>();

          for (
            let i = 0;
            i < numNodesToTrigger && triggeredIndices.size < config.nodes.length;
            i++
          ) {
            let randomIndex;
            do {
              randomIndex = Math.floor(Math.random() * config.nodes.length);
            } while (triggeredIndices.has(randomIndex));

            triggeredIndices.add(randomIndex);
            const randomNode = config.nodes[randomIndex];
            spread(randomNode, true);
          }

          config.autoTriggerTimer = 0;
        }
      }
    },
    [spread, drawCompletedConnection, drawStaticGrid, startCleanup, resetGrid]
  );

  const findClosestNode = useCallback((x: number, y: number): Node | null => {
    const config = configRef.current;
    let closest = null;
    const threshold = config.gridSize * 0.6;
    let minDistSquared = threshold * threshold;

    for (const node of config.nodes) {
      const dx = node.x - x;
      const dy = node.y - y;
      const distSquared = dx * dx + dy * dy;

      if (distSquared < minDistSquared) {
        minDistSquared = distSquared;
        closest = node;
      }
    }

    return closest;
  }, []);

  const animate = useCallback(() => {
    const config = configRef.current;
    const now = Date.now();
    const delta = Math.min(now - config.lastTime, 100);
    config.lastTime = now;

    update(delta);
    drawAnimation();

    config.animFrameId = requestAnimationFrame(animate);
  }, [update, drawAnimation]);

  const resizeCanvas = useCallback(() => {
    const config = configRef.current;
    const container = document.getElementById('grid-container');
    if (!container || !config.staticCanvas || !config.completedCanvas || !config.animCanvas) return;

    const rect = container.getBoundingClientRect();

    config.staticCanvas.width = rect.width;
    config.staticCanvas.height = rect.height;
    config.completedCanvas.width = rect.width;
    config.completedCanvas.height = rect.height;
    config.animCanvas.width = rect.width;
    config.animCanvas.height = rect.height;

    createGrid();
  }, [createGrid]);

  useEffect(
    function setupLoginCanvases() {
      const config = configRef.current;

      config.staticCanvas = document.getElementById('staticCanvas') as HTMLCanvasElement;
      config.completedCanvas = document.getElementById('completedCanvas') as HTMLCanvasElement;
      config.animCanvas = document.getElementById('animationCanvas') as HTMLCanvasElement;

      if (!config.staticCanvas || !config.completedCanvas || !config.animCanvas) return;

      config.staticCtx = config.staticCanvas.getContext('2d');
      config.completedCtx = config.completedCanvas.getContext('2d');
      config.animCtx = config.animCanvas.getContext('2d');

      resizeCanvas();

      const handleMouseMove = (e: MouseEvent) => {
        const rect = config.animCanvas!.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        config.hoveredNode = findClosestNode(x, y);
      };

      const handleClick = (e: MouseEvent) => {
        const rect = config.animCanvas!.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const node = findClosestNode(x, y);
        if (node) {
          spread(node, true);
          config.autoTriggerTimer = 0; // Reset auto-trigger timer on user interaction
        }
      };

      const handleMouseLeave = () => {
        config.hoveredNode = null;
      };

      config.animCanvas.addEventListener('mousemove', handleMouseMove);
      config.animCanvas.addEventListener('click', handleClick);
      config.animCanvas.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('resize', resizeCanvas);

      animate();

      return () => {
        cancelAnimationFrame(config.animFrameId);
        if (config.animCanvas) {
          config.animCanvas.removeEventListener('mousemove', handleMouseMove);
          config.animCanvas.removeEventListener('click', handleClick);
          config.animCanvas.removeEventListener('mouseleave', handleMouseLeave);
        }
        window.removeEventListener('resize', resizeCanvas);
        config.staticCtx = null;
        config.animCtx = null;
        config.completedCtx = null;
        config.staticCanvas = null;
        config.animCanvas = null;
        config.completedCanvas = null;
      };
    },
    [resizeCanvas, findClosestNode, spread, animate]
  );

  return (
    <div className="relative hidden lg:block overflow-hidden bg-[#0a0a0a]">
      <div id="grid-container" className="relative w-full h-full overflow-hidden bg-[#0a0a0a]">
        <canvas id="staticCanvas" className="absolute top-0 left-0 w-full h-full block" />
        <canvas id="completedCanvas" className="absolute top-0 left-0 w-full h-full block z-1" />
        <canvas
          id="animationCanvas"
          className="absolute top-0 left-0 w-full h-full block z-2 cursor-crosshair"
        />
      </div>
    </div>
  );
}
